import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { RequestQueryKeysE } from 'declarations/enums/react-query';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useCloseTicket } from 'hooks/api/useTickets';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

type UseTicketClosePopupT = {
  isClosingTicket: boolean;
  handleCloseTicket: (ticketIds: string[], requestRO: boolean, onClose: NoopT) => NoopT;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useTicketClosePopup = (): UseTicketClosePopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();

  /**
   * Api hooks
   */
  const closeTicketMutation = useCloseTicket({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
    },
  });

  /**
   * Handlers
   */
  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
    },
    []
  );
  const handleCloseTicket = useCallback(
    (ticketIds: string[], requestRo: boolean, onClose: NoopT) => () => {
      closeTicketMutation.mutate(
        { ticketIds, requestRo },
        {
          onSuccess: () => {
            dispatch(
              updateSnackbarNotification({
                snackbarMessage: 'Ticket successfully closed',
                snackBarSeverity: 'success',
              })
            );
            onClose();
          },
          onError: error => {
            const errorMsg =
              getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
            dispatch(
              updateSnackbarNotification({
                snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
                snackBarSeverity: 'error',
              })
            );
            onClose();
          },
        }
      );
    },
    [closeTicketMutation, dispatch]
  );

  return {
    isClosingTicket: closeTicketMutation.isLoading,
    handleCloseTicket,
    handleClosePopup,
  };
};

export default useTicketClosePopup;
